<template>
  <div class="jit-container">
    <header class="jit-header">
      <div class="header-content">
        <div class="header-text">
          <h1>Just-In-Time Admin Access</h1>
          <p class="header-subtitle">
            Request and manage temporary elevated admin access for client
            tenants
          </p>
        </div>
        <div v-if="activeAssignments.length > 0" class="header-quick-stats">
          <div class="quick-stat">
            <span class="quick-stat-value">{{ activeAssignments.length }}</span>
            <span class="quick-stat-label">Active Assignments</span>
          </div>
          <div class="quick-stat">
            <span class="quick-stat-value">{{ getTotalHoursRemaining() }}</span>
            <span class="quick-stat-label">Hours Remaining</span>
          </div>
        </div>
      </div>
    </header>

    <div class="jit-content">
      <!-- Request Form Section -->
      <div class="request-section">
        <div class="section-header">
          <h2>Request Admin Access</h2>
          <span class="section-badge">New Request</span>
        </div>

        <div class="request-form">
          <div class="form-row">
            <div class="form-group">
              <label for="client-select">Client Tenant</label>
              <select
                id="client-select"
                v-model="formData.selectedClient"
                class="form-control"
                :disabled="isSubmitting"
              >
                <option value="">-- Select Client --</option>
                <option v-for="client in clients" :key="client" :value="client">
                  {{ client }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="jira-ticket">Jira Ticket Reference</label>
              <input
                id="jira-ticket"
                v-model="formData.jiraTicket"
                type="text"
                class="form-control"
                placeholder="e.g., PROJ-1234"
                :disabled="isSubmitting"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="role-select">Role to Assign</label>
              <select
                id="role-select"
                v-model="formData.selectedRole"
                class="form-control"
                :disabled="isSubmitting"
              >
                <option value="">-- Select Role --</option>
                <option value="Global Administrator">
                  Global Administrator
                </option>
                <option value="Teams Administrator">Teams Administrator</option>
                <option value="Exchange Administrator">
                  Exchange Administrator
                </option>
                <option value="SharePoint Administrator">
                  SharePoint Administrator
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="duration-select">Assignment Period (Hours)</label>
              <select
                id="duration-select"
                v-model="formData.duration"
                class="form-control"
                :disabled="isSubmitting"
              >
                <option value="">-- Select Duration --</option>
                <option value="1">1 Hour</option>
                <option value="4">4 Hours</option>
                <option value="8">8 Hours</option>
                <option value="12">12 Hours</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="justification">Justification</label>
            <textarea
              id="justification"
              v-model="formData.justification"
              class="form-control"
              rows="3"
              placeholder="Provide a brief justification for this access request..."
              :disabled="isSubmitting"
            ></textarea>
          </div>

          <div class="form-actions">
            <button
              @click="submitRequest"
              :disabled="!isFormValid || isSubmitting"
              class="btn btn-primary"
            >
              <span v-if="isSubmitting">Processing...</span>
              <span v-else>Submit Request</span>
            </button>
            <button
              @click="resetForm"
              :disabled="isSubmitting"
              class="btn btn-secondary"
            >
              Clear Form
            </button>
          </div>
          <div v-if="pipelineStatus.message" class="status-section">
            <div :class="['status-card', pipelineStatus.type]">
              <div class="status-header">
                <span class="status-icon">{{
                  getStatusIcon(pipelineStatus.type)
                }}</span>
                <h3 class="status-title">
                  {{ getStatusTitle(pipelineStatus.type) }}
                </h3>
              </div>
              <p class="status-message">{{ pipelineStatus.message }}</p>
              <div v-if="pipelineStatus.progress" class="progress-section">
                <div class="progress-bar">
                  <div
                    class="progress-fill"
                    :style="{ width: pipelineStatus.progress + '%' }"
                  ></div>
                </div>
                <p class="progress-text">
                  {{ pipelineStatus.progress }}% complete
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Assignments Section -->
      <div
        v-if="activeAssignments.length > 0"
        class="active-assignments-section"
      >
        <div class="section-header">
          <h2>Active Assignments</h2>
          <button @click="refreshAssignments" class="btn btn-secondary btn-sm">
            Refresh
          </button>
        </div>

        <div class="assignments-grid">
          <div
            v-for="assignment in activeAssignments"
            :key="assignment.id"
            class="assignment-card"
          >
            <div class="assignment-header">
              <div class="assignment-client">
                <span class="client-badge">{{ assignment.client }}</span>
              </div>
              <div class="assignment-status">
                <span class="status-badge status-active">Active</span>
              </div>
            </div>

            <div class="assignment-details">
              <div class="detail-row">
                <span class="detail-label">Role:</span>
                <span class="detail-value">{{ assignment.role }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Jira Ticket:</span>
                <span class="detail-value ticket-link">{{
                  assignment.jiraTicket
                }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Expires:</span>
                <span class="detail-value">{{
                  formatExpiry(assignment.expiresAt)
                }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Time Remaining:</span>
                <span class="detail-value time-remaining">
                  {{ getTimeRemaining(assignment.expiresAt) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assignment History -->
      <div class="history-section">
        <div class="section-header">
          <h2>Assignment History</h2>
          <div class="history-filters">
            <input
              v-model="historyFilter"
              type="text"
              placeholder="Search history..."
              class="search-input"
            />
            <select v-model="statusFilter" class="filter-select">
              <option value="all">All Status</option>
              <option value="expired">Expired</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div v-if="isLoadingHistory" class="loading-state">
          <div class="loader"></div>
          <span>Loading history...</span>
        </div>

        <div v-else-if="!assignmentHistory.length" class="empty-state">
          <span>No assignment history found</span>
        </div>

        <div v-else class="history-table-wrapper">
          <table class="history-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Role</th>
                <th>Jira Ticket</th>
                <th>Requested By</th>
                <th>Duration</th>
                <th>Created</th>
                <th>Expired</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="record in paginatedHistory"
                :key="record.id"
                class="history-row"
              >
                <td>
                  <span class="client-badge">{{ record.client }}</span>
                </td>
                <td>{{ record.role }}</td>
                <td>
                  <span class="ticket-link">{{ record.jiraTicket }}</span>
                </td>
                <td>{{ record.requestedBy }}</td>
                <td>{{ record.duration }}h</td>
                <td>
                  <div class="date-cell">
                    <span class="date-primary">{{
                      formatDate(record.createdAt)
                    }}</span>
                    <span class="date-secondary">{{
                      formatTime(record.createdAt)
                    }}</span>
                  </div>
                </td>
                <td>
                  <div class="date-cell">
                    <span class="date-primary">{{
                      formatDate(record.expiredAt)
                    }}</span>
                    <span class="date-secondary">{{
                      formatTime(record.expiredAt)
                    }}</span>
                  </div>
                </td>
                <td>
                  <span :class="getStatusClass(record.status)">
                    {{ record.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="totalPages > 1" class="pagination">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="page-btn"
            >
              Previous
            </button>
            <span class="page-info"
              >Page {{ currentPage }} of {{ totalPages }}</span
            >
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="page-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchClients,
  getPipelineStatus,
  runJITPipeline,
  fetchJITAuditLogs,
} from "@/azure-devops-api";
import { msalInstance } from "@/auth";
import { mapState } from "vuex";
import { formatDate } from "@/services/date-utils.js";
import { getAccessToken } from "@/services/auth-utils.js";

export default {
  name: "JITAdminProcess",
  data() {
    return {
      clients: [],
      projectId: "1b238677-4a8d-4243-827e-88a0558c085a",
      repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
      jitAssignPipelineId: "230",
      jitListPipelineId: null, // TODO: Set your JIT list pipeline ID (optional)
      formData: {
        selectedClient: "",
        jiraTicket: "",
        selectedRole: "",
        duration: "",
        justification: "",
      },
      isSubmitting: false,
      isLoadingHistory: false,
      activeAssignments: [],
      assignmentHistory: [],
      runId: null,
      pollInterval: null,
      pipelineStatus: {
        message: "",
        type: "",
        progress: 0,
      },
      historyFilter: "",
      statusFilter: "all",
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),
    isFormValid() {
      return (
        this.formData.selectedClient &&
        this.formData.jiraTicket &&
        this.formData.selectedRole &&
        this.formData.duration &&
        this.formData.justification.trim()
      );
    },
    filteredHistory() {
      let filtered = [...this.assignmentHistory];

      if (this.historyFilter) {
        const search = this.historyFilter.toLowerCase();
        filtered = filtered.filter(
          (record) =>
            record.client.toLowerCase().includes(search) ||
            record.jiraTicket.toLowerCase().includes(search) ||
            record.role.toLowerCase().includes(search) ||
            record.requestedBy.toLowerCase().includes(search)
        );
      }

      if (this.statusFilter !== "all") {
        filtered = filtered.filter(
          (record) => record.status === this.statusFilter
        );
      }

      return filtered;
    },
    paginatedHistory() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredHistory.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredHistory.length / this.itemsPerPage);
    },
  },
  async mounted() {
    await this.loadClients();
    await this.loadActiveAssignments();
    await this.loadAssignmentHistory();
  },
  beforeUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },
  methods: {
    determineStatus(log) {
      if (!log) return "unknown";

      // Check explicit status first
      if (log.status && log.status.toLowerCase() === "failed") return "failed";

      // Check expiry
      if (log.expiryTime) {
        const now = new Date();
        const expiryTime = new Date(log.expiryTime);

        if (!isNaN(expiryTime.getTime())) {
          if (expiryTime < now) return "expired";
          return "active";
        }
      }

      return "active";
    },

    getStatusClass(status) {
      return [
        "status-badge",
        status === "active"
          ? "status-active"
          : status === "expired"
          ? "status-expired"
          : "status-failed",
      ];
    },

    async loadClients() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        this.clients = await fetchClients(
          accessToken.accessToken,
          this.repoId,
          "/Clients"
        );
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },
    async loadActiveAssignments() {
      try {
        const accessToken = await this.getAccessToken();

        // Fetch all audit logs
        const logs = await fetchJITAuditLogs(accessToken, this.repoId);

        // Filter for active assignments (not expired)
        const now = new Date();
        this.activeAssignments = logs
          .filter((log) => {
            const expiryTime = new Date(log.expiryTime);
            return expiryTime > now && log.status === "active";
          })
          .map((log) => ({
            id: log.assignmentId,
            client: log.clientCode,
            role: log.role,
            jiraTicket: log.jiraTicket,
            expiresAt: new Date(log.expiryTime),
            startedAt: new Date(log.startTime),
            requestedBy: log.requestedBy,
          }));

        console.log(
          `Loaded ${this.activeAssignments.length} active assignments`
        );
      } catch (error) {
        console.error("Error loading active assignments:", error);
        this.activeAssignments = [];
      }
    },

    async loadAssignmentHistory() {
      this.isLoadingHistory = true;
      try {
        const accessToken = await this.getAccessToken();

        console.log("Starting to load assignment history...");

        // Fetch all audit logs
        const logs = await fetchJITAuditLogs(accessToken, this.repoId);

        console.log("Raw logs received:", logs);
        console.log("Number of logs:", logs.length);

        if (logs.length > 0) {
          console.log("First log sample:", logs[0]);
        }

        // Map to history format with validation
        this.assignmentHistory = logs.map((log) => {
          console.log("Processing log:", log.assignmentId, log);

          return {
            id: log.assignmentId || "N/A",
            client: log.clientCode || "N/A",
            role: log.role || "N/A",
            jiraTicket: log.jiraTicket || "N/A",
            requestedBy: log.requestedBy || "N/A",
            duration: log.durationHours || 0,
            createdAt: log.startTime || log.timestamp,
            expiredAt: log.expiryTime,
            status: this.determineStatus(log),
          };
        });

        console.log("Mapped assignment history:", this.assignmentHistory);
        console.log(
          `Successfully loaded ${this.assignmentHistory.length} history records`
        );
      } catch (error) {
        console.error("Error loading assignment history:", error);
        this.assignmentHistory = [];
      } finally {
        this.isLoadingHistory = false;
      }
    },
    async submitRequest() {
      if (!this.isFormValid) return;

      if (!this.jitAssignPipelineId) {
        alert(
          "JIT Assignment pipeline not configured. Please set the pipeline ID."
        );
        return;
      }

      this.isSubmitting = true;
      this.pipelineStatus = {
        message: "Starting JIT admin assignment request...",
        type: "info",
        progress: 10,
      };

      try {
        const accessToken = await this.getAccessToken();

        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }
        const requestorUpn = accounts[0].username;

        // Trigger the JIT assignment pipeline
        this.runId = await runJITPipeline(
          accessToken,
          this.projectId,
          this.jitAssignPipelineId,
          {
            ClientCode: this.formData.selectedClient,
            JiraTicket: this.formData.jiraTicket,
            Role: this.formData.selectedRole,
            DurationHours: this.formData.duration,
            Justification: this.formData.justification,
            Requestor: requestorUpn,
          }
        );

        if (this.runId) {
          this.pipelineStatus = {
            message: "Assignment pipeline started. Monitoring progress...",
            type: "info",
            progress: 20,
          };

          // Start polling for status
          this.startPolling();
        } else {
          throw new Error("Failed to start pipeline");
        }
      } catch (error) {
        console.error("Error submitting request:", error);
        this.isSubmitting = false;
        this.pipelineStatus = {
          message: "Failed to submit request: " + error.message,
          type: "error",
        };
      }
    },
    resetForm() {
      this.formData = {
        selectedClient: "",
        jiraTicket: "",
        selectedRole: "",
        duration: "",
        justification: "",
      };
    },
    refreshAssignments() {
      this.loadActiveAssignments();
      this.loadAssignmentHistory();
    },
    startPolling() {
      this.pollInterval = setInterval(async () => {
        try {
          const accessToken = await this.getAccessToken();

          const status = await getPipelineStatus(
            accessToken,
            this.projectId,
            this.jitAssignPipelineId,
            this.runId
          );

          // Update progress based on status
          if (status.state === "inProgress") {
            this.pipelineStatus.progress = Math.min(
              this.pipelineStatus.progress + 10,
              90
            );
            this.pipelineStatus.message = `Processing assignment request... (${status.state})`;
          } else if (status.state === "completed") {
            clearInterval(this.pollInterval);
            this.isSubmitting = false;

            if (status.result === "succeeded") {
              this.pipelineStatus = {
                message: "Admin access assigned successfully!",
                type: "success",
                progress: 100,
              };

              this.resetForm();

              // Reload assignments
              await this.loadActiveAssignments();
              await this.loadAssignmentHistory();
            } else {
              this.pipelineStatus = {
                message: `Assignment failed: ${status.result}`,
                type: "error",
              };
            }
          }
        } catch (error) {
          console.error("Error polling status:", error);
          clearInterval(this.pollInterval);
          this.isSubmitting = false;
          this.pipelineStatus = {
            message: "Error checking pipeline status",
            type: "error",
          };
        }
      }, 5000); // Poll every 5 seconds
    },
    getTotalHoursRemaining() {
      const total = this.activeAssignments.reduce((sum, assignment) => {
        const hours = Math.max(
          0,
          (assignment.expiresAt - new Date()) / (1000 * 60 * 60)
        );
        return sum + hours;
      }, 0);
      return Math.round(total * 10) / 10;
    },
    getTimeRemaining(expiresAt) {
      const now = new Date();
      const diff = expiresAt - now;

      if (diff <= 0) return "Expired";

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return `${hours}h ${minutes}m`;
    },
    formatExpiry(date) {
      return new Date(date).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDate(dateString) {
      return formatDate(dateString, { mode: "short", locale: "en-US", fallback: "N/A" });
    },
    getAccessToken,

    formatTime(dateString) {
      if (!dateString) return "N/A";

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error("Invalid time:", dateString);
        return "Invalid Time";
      }

      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getStatusIcon(type) {
      const icons = {
        info: "⚙️",
        success: "✅",
        error: "❌",
        warning: "⚠️",
      };
      return icons[type] || "ℹ️";
    },
    getStatusTitle(type) {
      const titles = {
        info: "In Progress",
        success: "Success",
        error: "Error",
        warning: "Warning",
      };
      return titles[type] || "Status";
    },
  },
};
</script>

<style scoped>
.jit-container {
  padding: 2rem;
}

.jit-header {
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #bfdbfe;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.header-text h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.header-subtitle {
  color: #64748b;
  font-size: 1rem;
}

.header-quick-stats {
  display: flex;
  gap: 2rem;
}

.quick-stat {
  text-align: center;
}

.quick-stat-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #3b82f6;
}

.quick-stat-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}

.jit-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.request-section,
.active-assignments-section,
.history-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.section-badge {
  padding: 0.25rem 0.75rem;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.request-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s;
  font-family: inherit;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control:disabled {
  background-color: #f1f5f9;
  cursor: not-allowed;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.375rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-secondary {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e2e8f0;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #dc2626;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.assignments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.assignment-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  transition: all 0.2s;
}

.assignment-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.assignment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.client-badge {
  background: #ffffff;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  border: 1px solid #e2e8f0;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-active {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.status-expired {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.status-failed {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.assignment-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.detail-label {
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  color: #1e293b;
  font-weight: 500;
}

.ticket-link {
  color: #3b82f6;
  font-family: monospace;
}

.time-remaining {
  color: #16a34a;
  font-weight: 600;
}

.history-filters {
  display: flex;
  gap: 0.75rem;
}

.search-input,
.filter-select {
  padding: 0.5rem 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s;
  font-family: inherit;
}

.search-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #94a3b8;
  font-size: 1rem;
  gap: 1rem;
}

.loader {
  border: 2px solid #f1f5f9;
  border-radius: 50%;
  border-top: 2px solid #3b82f6;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.history-table-wrapper {
  overflow-x: auto;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table th {
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: #f8fafc;
}

.history-table td {
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.875rem;
}

.history-row:hover {
  background-color: #f8fafc;
}

.date-cell {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.date-primary {
  font-weight: 500;
  color: #1e293b;
}

.date-secondary {
  font-size: 0.75rem;
  color: #94a3b8;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.page-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #475569;
}

.page-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  color: #64748b;
}

/* Status Display */
.status-section {
  margin-top: 1.5rem;
}

.status-card {
  padding: 1.25rem;
  border-radius: 0.5rem;
  border-left: 4px solid;
}

.status-card.info {
  background: #e7f3ff;
  border-color: #3b82f6;
}

.status-card.success {
  background: #d4edda;
  border-color: #16a34a;
}

.status-card.error {
  background: #f8d7da;
  border-color: #ef4444;
}

.status-card.warning {
  background: #fff3cd;
  border-color: #ffc107;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.status-icon {
  font-size: 1.25rem;
}

.status-title {
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
}

.status-message {
  margin: 0;
  color: #475569;
  font-size: 0.875rem;
}

.progress-section {
  margin-top: 1rem;
}

.progress-bar {
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  transition: width 0.3s ease;
}

.progress-text {
  margin: 0.5rem 0 0 0;
  font-size: 0.8125rem;
  color: #64748b;
}
</style>
